import { DiscountType, Voucher } from '@/app/shared/interface/voucher.interface';
import { isPlatformBrowser } from '@angular/common';
import { Component, EventEmitter, Inject, Input, Output, PLATFORM_ID, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ProductService } from "@/app/shared/services/product.service";
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-voucher-detail-modal',
  templateUrl: './voucher-detail-modal.component.html',
  styleUrl: './voucher-detail-modal.component.scss'
})
export class VoucherDetailModalComponent {

  @ViewChild("detailModal", { static: false }) detailModal: TemplateRef<any>;

  @Input() selectedVoucher: Voucher;

  @Output() closeModalEvent = new EventEmitter<void>();

  public modalOpen: boolean = false;

  private searchChanged$ = new Subject<string>();
  private lastKeyword: string = '';

  paginate = {
    currentPage: 1,
    totalItems: 0,
    pageSize: 12
  };
  public isLoadingProducts = false;
  productSearchKeyword: string = '';

  discountType = DiscountType;

  constructor(
    private modalService: NgbModal,
    private toastService: ToastrService,
    private productService: ProductService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    this.searchChanged$
      .pipe(
        debounceTime(200),
        distinctUntilChanged()
      )
      .subscribe((keyword) => {
        const trimmed = keyword.trim();
        if (trimmed !== this.lastKeyword) {
          this.lastKeyword = trimmed;
          this.paginate.currentPage = 1;
          this.loadApplicableProductsForVoucher(this.selectedVoucher);
        }
      });
  }

  onSearchKeywordChange(): void {
    this.searchChanged$.next(this.productSearchKeyword);
  }

  triggerSearch(): void {
    const trimmed = this.productSearchKeyword.trim();
    if (trimmed !== this.lastKeyword) {
      this.lastKeyword = trimmed;
      this.paginate.currentPage = 1;
      this.loadApplicableProductsForVoucher(this.selectedVoucher);
    }
  }
  async openModal() {
    if (isPlatformBrowser(this.platformId)) { // For SSR
      if (!this.selectedVoucher) return;

      this.productSearchKeyword = '';
      this.lastKeyword = '';
      this.paginate.currentPage = 1;

      if (!this.selectedVoucher.applicableProducts?.length) {
        await this.loadApplicableProductsForVoucher(this.selectedVoucher);
      }

      this.modalOpen = true;
      this.modalService.open(this.detailModal, {
        ariaLabelledBy: 'Confirmation-Modal',
        centered: true,
        windowClass: 'theme-modal text-center voucher-detail-modal',
        size: 'lg'
      }).result.then(
        (result) => { /* no-op */ },
        (reason) => {
          this.modalOpen = false;
          this.closeModalEvent.emit();
        }
      );
    }
  }


  closeModal() {
    this.modalService.dismissAll();
  }

  copyToClipboard(voucher: Voucher) {
    if (isPlatformBrowser(this.platformId)) {
      navigator.clipboard.writeText(voucher.code);
    }
    this.toastService.success("Sao chép mã giảm giá thành công!", "", { positionClass: "toast-top-right" });
  }


  loadApplicableProductsForVoucher(voucher: Voucher): Promise<void> {
    const condition = voucher.voucherCondition;
    const productSkus = condition?.productQuantityConditions?.map((c: any) => c.sku) || [];
    const labels = condition?.applicableLabels ?? [];
    const categoryIds = condition?.applicableCategoryIds ?? [];
    const labelsQuantity = condition?.applicableLabelQuantity?.map((c: any) => c.labels) || [];
    const categoryQuantityIds = condition?.applicableCategoryQuantity?.map((c: any) => c.categoryId) || [];

    const body: any = {
      limit: this.paginate.pageSize,
      offset: (this.paginate.currentPage - 1) * this.paginate.pageSize
    };

    const hasProductIds = (voucher?.conditions?.productIds?.length ?? 0) > 0;
    const excludedIds = voucher?.conditions?.excludedProductIds?.map(id => String(id)) || [];

    if (hasProductIds) {
      body.productIds = voucher.conditions?.productIds?.filter(id => !excludedIds.includes(String(id)));
      
      if (body.productIds?.length === 0) {
        voucher.applicableProducts = [];
        this.paginate.totalItems = 0;
        this.isLoadingProducts = false;
        return Promise.resolve();
      }
    } else {
      const hasVoucherCondition = productSkus.length > 0 || labels.length > 0 || categoryIds.length > 0 || labelsQuantity.length > 0 || categoryQuantityIds.length > 0;
      if (hasVoucherCondition) {
        if (productSkus.length) body.productSkus = productSkus;
        if (labels.length) body.labels = labels;
        if (categoryIds.length) body.categories = categoryIds;
        if (labelsQuantity.length) body.labels = labelsQuantity;
        if (categoryQuantityIds.length) body.categories = categoryIds;
      }
    }

    if (excludedIds.length > 0) {
      body.excludedProductIds = voucher.conditions?.excludedProductIds;
    }

    if (this.productSearchKeyword?.trim()) {
      body.searchTerm = this.productSearchKeyword.trim();
    }

    this.isLoadingProducts = true;

    return new Promise((resolve) => {
      this.productService.getProductsForMember(JSON.stringify(body)).subscribe({
        next: (res: any) => {
          let products = res?.products || [];
          if (excludedIds.length > 0) {
            products = products.filter((p: any) => !excludedIds.includes(String(p.productID)));
          }
          voucher.applicableProducts = products;
          this.paginate.totalItems = res.total ?? products.length;
          resolve();
        },
        error: (err) => {
          console.warn(`Không load được sản phẩm cho voucher ${voucher.code}`, err);
          resolve();
        },
        complete: () => {
          this.isLoadingProducts = false;
        }
      });
    });
  }



  async onPageChanged(page: number) {
    const self = this;
    self.paginate.currentPage = page;
    await self.loadApplicableProductsForVoucher(self.selectedVoucher);
    const modalContent = document.querySelector('.detail-container');
    if (modalContent) {
      modalContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }


  // ngOnDestroy() {
  //   if (this.modalOpen) {
  //     this.modalService.dismissAll();
  //   }
  // }
}
